/**
 * KAN-1962 — the picked options the collapsed landing card shows: the fields
 * the admin marked ``landing_summary`` (at most three), each as its label and
 * the value the sample is currently priced with.
 */

type Field = Record<string, unknown>;

/** Most picked options the collapsed card has room for. */
export const MAX_SUMMARY_FIELDS = 3;

export function summaryFields(fields: Field[] | undefined): Field[] {
  return (fields ?? [])
    .filter((f) => f.landing_summary === true)
    .slice(0, MAX_SUMMARY_FIELDS);
}

const text = (v: unknown): string | null =>
  typeof v === "string" && v.trim() ? v : null;

export function summaryLabel(field: Field, lang: string): string {
  const cs = text(field.label_cs);
  const en = text(field.label_en);
  return (lang.startsWith("cs") ? (cs ?? en) : (en ?? cs)) ?? String(field.key);
}

/** The shown value; ``null`` when the field has none yet. */
export function summaryValue(
  field: Field,
  value: unknown,
  {
    lang,
    materials,
    lineMaterial,
    yesNo,
  }: {
    lang: string;
    materials: Array<{ code: string; display_name: string }>;
    /** Display name of the line's material — what a material field without
     *  its own value shows. */
    lineMaterial: string | null;
    yesNo: (v: boolean) => string;
  },
): string | null {
  if (field.kind === "material_ref") {
    const own = materials.find((m) => m.code === value)?.display_name;
    return own ?? lineMaterial;
  }
  if (typeof value === "boolean") return yesNo(value);
  if (typeof value === "number") {
    const unit = text(field.display_unit) ?? text(field.unit);
    const n = new Intl.NumberFormat(lang).format(value);
    return unit ? `${n} ${unit}` : n;
  }
  if (typeof value === "string" && value) {
    const options = Array.isArray(field.enum_options)
      ? (field.enum_options as Field[])
      : [];
    const option = options.find((o) => o.value === value);
    return option ? summaryLabel({ ...option, key: value }, lang) : value;
  }
  return null;
}
